/**
 * localStorage-backed adapter.
 *
 * Progress lives under one key per profile, so writing one profile never
 * rewrites another and quota pressure stays bounded (a 59-item set is ~6 KB).
 * Cross-tab updates come free via the `storage` event.
 */

import { emptySnapshot, isBackupV1 } from '@/domain/progress.types';
import type {
  BackupV1,
  Profile,
  ProgressPatch,
  ProgressSnapshot,
  SetProgress,
} from '@/domain/progress.types';
import {
  applyPatchesTo,
  mergeSnapshots,
  type StorageAdapter,
  type Unsubscribe,
} from './StorageAdapter';

const NS = 'meow:v1';
const K_SCHEMA = `${NS}:schema`;
const K_PROFILES = `${NS}:profiles`;
const K_PROGRESS = `${NS}:progress:`;

const APP_VERSION = '0.1.0';

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `p_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Storage access can throw outright, not just return null — Safari private
 * mode, disabled site data, sandboxed frames. Every access is guarded so the
 * app degrades to in-memory rather than failing to boot.
 */
function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    globalThis.localStorage?.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readJson<T>(key: string, fallback: T): T {
  const raw = safeGet(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export class LocalStorageAdapter implements StorageAdapter {
  readonly id = 'local' as const;
  readonly capabilities = { multiProfile: true, remote: false, realtime: false };

  /** Mirrors what is on disk when localStorage is unavailable. */
  private readonly memory = new Map<string, string>();
  private usingMemory = false;

  private get(key: string): string | null {
    return this.usingMemory ? (this.memory.get(key) ?? null) : safeGet(key);
  }

  private set(key: string, value: string): void {
    if (!this.usingMemory && safeSet(key, value)) return;
    this.usingMemory = true;
    this.memory.set(key, value);
  }

  private remove(key: string): void {
    this.memory.delete(key);
    if (!this.usingMemory) safeRemove(key);
  }

  private readJsonScoped<T>(key: string, fallback: T): T {
    const raw = this.get(key);
    if (raw === null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  /** Shared by concurrent callers so seeding can never run twice. */
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    this.initPromise ??= (async () => {
      const schema = readJson<{ version?: number }>(K_SCHEMA, {});
      if (schema.version !== 1) this.set(K_SCHEMA, JSON.stringify({ version: 1 }));
      const profiles = await this.listProfiles();
      if (profiles.length === 0) await this.createProfile('Main');
    })();
    return this.initPromise;
  }

  async listProfiles(): Promise<Profile[]> {
    return this.readJsonScoped<Profile[]>(K_PROFILES, []);
  }

  private writeProfiles(profiles: Profile[]): void {
    this.set(K_PROFILES, JSON.stringify(profiles));
  }

  async createProfile(name: string): Promise<Profile> {
    const profiles = await this.listProfiles();
    const now = Date.now();
    const profile: Profile = { id: newId(), name: name.trim() || 'Main', createdAt: now, updatedAt: now };
    this.writeProfiles([...profiles, profile]);
    this.writeSnapshot(emptySnapshot(profile.id));
    return profile;
  }

  async renameProfile(id: string, name: string): Promise<void> {
    const profiles = await this.listProfiles();
    this.writeProfiles(
      profiles.map((p) => (p.id === id ? { ...p, name: name.trim() || p.name, updatedAt: Date.now() } : p)),
    );
  }

  async deleteProfile(id: string): Promise<void> {
    const profiles = await this.listProfiles();
    this.writeProfiles(profiles.filter((p) => p.id !== id));
    this.remove(K_PROGRESS + id);
  }

  async duplicateProfile(id: string, name: string): Promise<Profile> {
    const source = await this.getProgress(id);
    const profile = await this.createProfile(name);
    this.writeSnapshot({
      ...structuredClone(source),
      profileId: profile.id,
      rev: 0,
      updatedAt: Date.now(),
    });
    return profile;
  }

  async getProgress(profileId: string): Promise<ProgressSnapshot> {
    const snapshot = this.readJsonScoped<ProgressSnapshot | null>(K_PROGRESS + profileId, null);
    if (!snapshot || snapshot.schemaVersion !== 1) return emptySnapshot(profileId);
    return { ...snapshot, profileId };
  }

  private writeSnapshot(snapshot: ProgressSnapshot): void {
    this.set(K_PROGRESS + snapshot.profileId, JSON.stringify(snapshot));
  }

  async applyPatches(profileId: string, patches: ProgressPatch[]): Promise<ProgressSnapshot> {
    const next = applyPatchesTo(await this.getProgress(profileId), patches);
    this.writeSnapshot(next);
    return next;
  }

  /**
   * Clears by stamping every entry `done: false`, not by deleting them.
   *
   * The two read identically — every call site asks `?.done === true` — but
   * `mergeSnapshots` is a union with no tombstones, so a deleted entry is
   * simply reinstated by the next device that syncs, and the reset appears to
   * undo itself. A newer `done: false` is a fact that travels.
   */
  async clearSet(profileId: string, setId: string): Promise<ProgressSnapshot> {
    const current = await this.getProgress(profileId);
    const existing = current.sets[setId];
    if (!existing) return current;
    const now = Date.now();
    const cleared: SetProgress = {};
    for (const [itemId, entry] of Object.entries(existing)) {
      cleared[itemId] = { ...entry, done: false, updatedAt: now };
    }
    const next = {
      ...current,
      sets: { ...current.sets, [setId]: cleared },
      rev: current.rev + 1,
      updatedAt: now,
    };
    this.writeSnapshot(next);
    return next;
  }

  async pruneOrphans(
    profileId: string,
    liveIdsBySet: Record<string, string[]>,
  ): Promise<ProgressSnapshot> {
    const current = await this.getProgress(profileId);
    const sets: ProgressSnapshot['sets'] = {};
    for (const [setId, setProgress] of Object.entries(current.sets)) {
      const live = new Set(liveIdsBySet[setId] ?? []);
      // A set with no live ids is unknown content, not dead content — keep it.
      if (live.size === 0) {
        sets[setId] = setProgress;
        continue;
      }
      sets[setId] = Object.fromEntries(
        Object.entries(setProgress).filter(([itemId]) => live.has(itemId)),
      );
    }
    const next = { ...current, sets, rev: current.rev + 1, updatedAt: Date.now() };
    this.writeSnapshot(next);
    return next;
  }

  subscribe(profileId: string, cb: (snapshot: ProgressSnapshot) => void): Unsubscribe {
    if (typeof window === 'undefined') return () => {};
    const key = K_PROGRESS + profileId;
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== key || event.newValue === null) return;
      try {
        cb(JSON.parse(event.newValue) as ProgressSnapshot);
      } catch {
        /* ignore malformed cross-tab payloads */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }

  async exportBackup(): Promise<BackupV1> {
    const profiles = await this.listProfiles();
    const progress: Record<string, ProgressSnapshot> = {};
    for (const p of profiles) progress[p.id] = await this.getProgress(p.id);
    return {
      format: 'meowtracker-backup',
      version: 1,
      exportedAt: new Date().toISOString(),
      app: { version: APP_VERSION },
      profiles,
      progress,
    };
  }

  async importBackup(backup: BackupV1, mode: 'replace' | 'merge'): Promise<void> {
    if (!isBackupV1(backup)) throw new Error('Not a MeowTracker backup file.');

    if (mode === 'replace') {
      for (const p of await this.listProfiles()) this.remove(K_PROGRESS + p.id);
      this.writeProfiles(backup.profiles);
      for (const [profileId, snapshot] of Object.entries(backup.progress)) {
        this.writeSnapshot({ ...snapshot, profileId });
      }
      return;
    }

    const existing = await this.listProfiles();
    const byId = new Map(existing.map((p) => [p.id, p]));
    for (const p of backup.profiles) if (!byId.has(p.id)) byId.set(p.id, p);
    this.writeProfiles([...byId.values()]);

    for (const [profileId, incoming] of Object.entries(backup.progress)) {
      const base = await this.getProgress(profileId);
      this.writeSnapshot(mergeSnapshots(base, { ...incoming, profileId }));
    }
  }
}
